import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { CredentialsService } from '@app/core';

@Injectable({
  providedIn: 'root'
})
export class GdTrackingService implements OnDestroy {
  private sessionId: string | null = null;
  private customerId: string | null = null;
  private system: string | null = null;
  private heartbeatInterval: any = null;
  private currentPath: string | null = null;
  private pendingPageView: string | null = null;
  private readonly HEARTBEAT_MS = 30000;

  constructor(private http: HttpClient, private router: Router, private credentialsService: CredentialsService) {}

  identify(params: { customerId: string; system: string; userId?: string }) {
    this.customerId = params.customerId;
    this.system = params.system;

    const body: any = {
      customerId: params.customerId,
      system: params.system
    };
    if (params.userId) {
      body.userId = params.userId;
    }

    this.post('/sessions', body).subscribe(
      (res: any) => {
        this.sessionId = res && res.data && res.data.sessionId ? res.data.sessionId : null;
        if (this.sessionId) {
          this.startHeartbeat();
          if (this.pendingPageView) {
            this.trackPageView(this.pendingPageView);
            this.pendingPageView = null;
          }
        }
      },
      () => {}
    );
  }

  autoIdentifyFromUrl(url: string) {
    if (this.sessionId) return;
    const match = url.match(/\/client\/(\d+)\//);
    if (!match) return;
    const customerId = match[1];
    try {
      const creds = JSON.parse(localStorage.getItem('credentials'));
      if (!creds || !creds.id) return;
      this.pendingPageView = url;
      this.identify({
        customerId,
        system: 'backoffice_old',
        userId: String(creds.id)
      });
    } catch {}
  }

  trackPageView(url: string) {
    if (!this.sessionId || !this.customerId || !this.system) return;
    let pagePath: string;
    try {
      pagePath = new URL(url).pathname;
    } catch {
      pagePath = url;
    }
    this.currentPath = pagePath;
    this.post('/events', {
      events: [
        {
          sessionId: this.sessionId,
          customerId: this.customerId,
          system: this.system,
          eventType: 'pageview',
          pagePath,
          pageTitle: document.title
        }
      ]
    }).subscribe(
      () => {},
      () => {}
    );
  }

  trackEvent(eventType: string, eventName?: string, properties?: Record<string, any>) {
    if (!this.sessionId || !this.customerId || !this.system) return;
    this.post('/events', {
      events: [
        {
          sessionId: this.sessionId,
          customerId: this.customerId,
          system: this.system,
          eventType,
          eventName,
          pagePath: this.currentPath,
          properties: properties || {}
        }
      ]
    }).subscribe(
      () => {},
      () => {}
    );
  }

  endSession() {
    if (!this.sessionId) return;
    this.stopHeartbeat();
    this.post(`/sessions/${this.sessionId}/end`, {}).subscribe(
      () => {},
      () => {}
    );
    this.sessionId = null;
    this.customerId = null;
    this.system = null;
  }

  ngOnDestroy() {
    this.stopHeartbeat();
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (!this.sessionId) return;
      this.post('/heartbeat', {
        sessionId: this.sessionId,
        pagePath: this.currentPath
      }).subscribe(
        () => {},
        () => {}
      );
    }, this.HEARTBEAT_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private post(path: string, body: any) {
    const token = (this.credentialsService.credentials && this.credentialsService.credentials.token) || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token
    });
    return this.http.post(`${environment.trackingServiceUrl}${path}`, body, { headers });
  }
}
